import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';
import { formatNumber } from '../../utils/formatters.js';

const STATUS_VARIANT = { OPEN: 'warning', ACCEPTED: 'success', REJECTED: 'danger' };
const RESULT_VARIANT = { OK: 'success', NOK: 'danger' };

export function InspectionLotDetailModal({ lotId, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [newChar, setNewChar] = useState({ name: '', result: '' });
  const [error, setError] = useState(null);

  const detail = useQuery({ queryKey: ['inventory', 'inspection-lot', lotId], queryFn: () => inventoryApi.getInspectionLot(lotId).then((r) => r.items), enabled: Boolean(lotId) });
  const lot = detail.data;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['inventory', 'inspection-lots'] });
    queryClient.invalidateQueries({ queryKey: ['inventory', 'inspection-lot', lotId] });
  };
  const run = (fn) => ({ mutationFn: fn, onSuccess: () => { setError(null); invalidate(); }, onError: (e) => setError(e?.message || t('common.loadError')) });
  const accept = useMutation(run(() => inventoryApi.acceptLot(lotId)));
  const reject = useMutation(run(() => inventoryApi.rejectLot(lotId)));
  const addChar = useMutation(run(() => inventoryApi.addLotCharacteristic(lotId, { name: newChar.name.trim(), result: newChar.result || null })));
  const busy = accept.isPending || reject.isPending;

  return (
    <Modal
      open={Boolean(lotId)}
      onClose={onClose}
      title={lot ? `${lot.lotNumber} · ${lot.productSku}` : t('qm.detail.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.close')}</Button>
          {lot?.status === 'OPEN' && (
            <>
              <Button variant="danger" disabled={busy} onClick={() => reject.mutate()}>{t('qm.action.reject')}</Button>
              <Button variant="primary" disabled={busy} onClick={() => accept.mutate()}>{t('qm.action.accept')}</Button>
            </>
          )}
        </>
      }
    >
      {lot && (
        <div className="form-stack">
          <p className="muted text-sm" style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            <Badge variant={STATUS_VARIANT[lot.status]}>{t(`qm.status.${lot.status}`)}</Badge>
            {lot.productName} · {formatNumber(lot.quantity)} {t('qm.col.quantity')} · {t(`qm.source.${lot.source}`)}
          </p>
          {lot.status === 'REJECTED' && <div className="alert danger">{t('qm.detail.blocked', { qty: formatNumber(lot.quantity) })}</div>}
          {lot.decisionNote && <div className="alert info">{lot.decisionNote}</div>}

          <div className="settings-divider">{t('qm.field.characteristics')}</div>
          {(lot.characteristics || []).length === 0 && <p className="muted text-sm">{t('qm.noChars')}</p>}
          {(lot.characteristics || []).map((c) => (
            <div key={c.id} className="field-row" style={{ alignItems: 'center' }}>
              <span style={{ flex: 1 }}>{c.name}{c.comment ? <span className="muted text-sm"> - {c.comment}</span> : null}</span>
              {c.result && <Badge variant={RESULT_VARIANT[c.result]}>{t(`qm.result.${c.result}`)}</Badge>}
            </div>
          ))}

          {lot.status === 'OPEN' && (
            <div className="field-row">
              <input className="input" placeholder={t('qm.field.charName')} value={newChar.name} onChange={(e) => setNewChar((c) => ({ ...c, name: e.target.value }))} />
              <select className="input" style={{ maxWidth: 110 }} value={newChar.result} onChange={(e) => setNewChar((c) => ({ ...c, result: e.target.value }))}>
                <option value="">-</option>
                <option value="OK">{t('qm.result.OK')}</option>
                <option value="NOK">{t('qm.result.NOK')}</option>
              </select>
              <Button variant="secondary" disabled={addChar.isPending || !newChar.name.trim()} onClick={() => { addChar.mutate(); setNewChar({ name: '', result: '' }); }}>{t('qm.field.addChar')}</Button>
            </div>
          )}
          {error && <div className="alert danger">{error}</div>}
        </div>
      )}
    </Modal>
  );
}
